import { useTranslation } from '@pancakeswap/localization'
import { ChainId, ERC20Token, Token } from '@pancakeswap/sdk'
import { ChevronDownIcon, Link, RefreshIcon } from '@pancakeswap/uikit'
import { useId, useState } from 'react'
import { useUserTokenRisk } from 'state/user/hooks/useUserTokenRisk'
import styled, { DefaultTheme, css, keyframes } from 'styled-components'
import useSWRImmutable from 'swr/immutable'
import type { RiskFlag, RiskSeverity, TokenRiskFacts, TokenRiskReport } from 'server/tokenRisk/types'
import { fetchRiskToken } from 'views/Swap/hooks/fetchTokenRisk'

/**
 * Token risk check (GoPlus Security, asked by our server: /api/risk/56/<address>).
 * One reserved 32px row: source + check time on the left, a status chip on the right. The chip opens
 * the details (flags, facts, source, disclaimer). Wording rules: never "safe"; no answer is "No data"
 * or "Not checked yet", never a pass. Mapping rules live in server/tokenRisk/goplus.ts.
 */

export const DISCLAIMER = 'Automated check by GoPlus. Not a safety rating. Always do your own research.'
export const goPlusTokenPage = (address: string) => `https://gopluslabs.io/token-security/56/${address}`

interface AccessRiskProps {
  token: ERC20Token
}

export function useTokenRisk(token?: Token) {
  return useSWRImmutable(
    token && token.address && token.chainId === ChainId.BSC && ['risk', token.chainId, token.address],
    () => fetchRiskToken(token.address, token.chainId),
    { shouldRetryOnError: false },
  )
}

type ViewState = 'loading' | 'high' | 'caution' | 'clear' | 'partial' | 'not_checked' | 'unavailable'
type Tone = 'high' | 'caution' | 'neutral'

const TONE_OF_STATE: Record<ViewState, Tone> = {
  loading: 'neutral',
  high: 'high',
  caution: 'caution',
  clear: 'neutral',
  partial: 'neutral',
  not_checked: 'neutral',
  unavailable: 'neutral',
}

const toneBorder = (theme: DefaultTheme, tone: Tone) =>
  tone === 'high' ? theme.colors.failure : tone === 'caution' ? theme.colors.warning : theme.colors.cardBorder
const toneBg = (theme: DefaultTheme, tone: Tone) =>
  tone === 'high' ? theme.colors.failure33 : tone === 'caution' ? theme.colors.warning33 : 'transparent'
const severityColor = (theme: DefaultTheme, s: RiskSeverity | Tone) =>
  s === 'high' ? theme.colors.failure : s === 'caution' ? theme.colors.warning : theme.colors.textSubtle

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`

const Wrap = styled.div`
  width: 100%;
  min-width: 0;
`

/** Reserved height: loading, result and error rows are all 32px, so nothing below moves when the answer lands. */
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-height: 32px;
  min-width: 0;
`

const Source = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const Chip = styled.button<{ $tone: Tone }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  max-width: 100%;
  padding: 0 8px 0 10px;
  border-radius: 14px;
  border: 1px solid ${({ theme, $tone }) => toneBorder(theme, $tone)};
  background: ${({ theme, $tone }) => toneBg(theme, $tone)};
  color: ${({ theme, $tone }) => ($tone === 'neutral' ? theme.colors.textSubtle : theme.colors.text)};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  svg {
    flex: none;
    transition: transform 0.15s ease;
  }
  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
`

const Dot = styled.span<{ $color: RiskSeverity | Tone; $pulse?: boolean }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme, $color }) => severityColor(theme, $color)};
  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${pulse} 1.2s ease-in-out infinite;
    `}
`

const RetryButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 14px;
  background: transparent;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
  }
`

const Panel = styled.div`
  display: grid;
  gap: 12px;
  margin: 4px 0 8px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.tertiary};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
`

const FlagList = styled.ul`
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`

const FlagItem = styled.li`
  display: grid;
  grid-template-columns: 8px 1fr;
  column-gap: 8px;
  align-items: baseline;
  ${Dot} {
    transform: translateY(-1px);
  }
`

const Detail = styled.span`
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(112px, 1fr));
  gap: 8px 12px;
  margin: 0;
  div {
    min-width: 0;
  }
  dt {
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  dd {
    margin: 0;
    font-weight: 600;
  }
`

const Disclaimer = styled.p`
  margin: 0;
  font-weight: 600;
`

const Meta = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const viewStateOf = (report: TokenRiskReport | undefined, error: unknown): ViewState => {
  if (error) return 'unavailable'
  if (!report) return 'loading'
  if (report.status === 'unavailable') return 'unavailable'
  if (report.status === 'no_data') return 'not_checked'
  return report.summary === 'no_data' ? 'not_checked' : report.summary
}

const pct = (x: number | null) => (x === null ? null : `${Number((x * 100).toFixed(1))}%`)

/** "3%" when buy and sell tax round the same, else "2% / 2.9%" (buy / sell). */
const taxChip = (facts: TokenRiskFacts | null) => {
  const buy = pct(facts?.buyTax ?? null) ?? '?'
  const sell = pct(facts?.sellTax ?? null) ?? '?'
  return buy === sell ? buy : `${buy} / ${sell}`
}

function FactsList({ facts }: { facts: TokenRiskFacts }) {
  const { t } = useTranslation()
  const unknown = t('Unknown')
  const yesNo = (v: boolean | null, y: string, n: string) => (v === null ? unknown : v ? y : n)
  const rows: [string, string][] = [
    [t('Buy tax'), pct(facts.buyTax) ?? unknown],
    [t('Sell tax'), pct(facts.sellTax) ?? unknown],
    [t('Holders'), facts.holderCount === null ? unknown : facts.holderCount.toLocaleString('en-US')],
    [t('LP locked (V2)'), pct(facts.lpLockedShare) ?? t('No V2 LP data')],
    [t('Source code'), yesNo(facts.openSource, t('Verified'), t('Not verified'))],
    [t('Upgradeable'), yesNo(facts.proxy, t('Yes'), t('No'))],
  ]
  return (
    <Facts data-testid="token-risk-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </Facts>
  )
}

function FlagRows({ flags }: { flags: RiskFlag[] }) {
  const { t } = useTranslation()
  return (
    <FlagList>
      {flags.map((f) => (
        <FlagItem key={f.id} data-severity={f.severity}>
          <Dot $color={f.severity} aria-hidden />
          <span>
            {f.severity === 'high' ? `${t('Red flag')}: ` : f.severity === 'caution' ? `${t('Check')}: ` : ''}
            {f.label}
            {f.detail && <Detail>{f.detail}</Detail>}
          </span>
        </FlagItem>
      ))}
    </FlagList>
  )
}

const formatTime = (ms: number, withDate = false) =>
  new Date(ms).toLocaleString(undefined, {
    ...(withDate ? { day: 'numeric', month: 'short' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  })

const GoPlusRiskCheck: React.FC<AccessRiskProps> = ({ token }) => {
  const { t } = useTranslation()
  const panelId = useId()
  const [open, setOpen] = useState(false)
  const { data, error, mutate, isValidating } = useTokenRisk(token)
  const report = error ? undefined : data?.report
  const state = viewStateOf(report, error)
  const tone = TONE_OF_STATE[state]

  const highs = report?.flags.filter((f) => f.severity === 'high') ?? []
  const cautions = report?.flags.filter((f) => f.severity === 'caution') ?? []
  // A small tax (0-10 %) is only a note, but it changes what you receive: the chip shows it instead of "No major flags".
  const smallTax = state === 'clear' && report?.flags.some((f) => f.id === 'tax') ? taxChip(report.facts) : null

  const label = {
    loading: t('Checking…'),
    high: highs.length === 1 ? `${t('Red flag')}: ${highs[0]?.short}` : t('%count% red flags', { count: highs.length }),
    caution: cautions.length === 1 ? `${t('Check')}: ${cautions[0]?.short}` : t('%count% flags to check', { count: cautions.length }),
    clear: smallTax ? `${t('Tax')} ${smallTax}` : t('No major flags'),
    partial: t('Partial check'),
    not_checked: t('Not checked yet'),
    unavailable: t('No data'),
  }[state]

  const source =
    report?.checkedAt && state !== 'unavailable' ? `GoPlus · ${formatTime(report.checkedAt)}` : 'GoPlus Security'

  const reasonText =
    report?.reason === 'rate_limited'
      ? t('GoPlus is busy (rate limit).')
      : report?.reason === 'timeout'
      ? t('GoPlus did not answer in time.')
      : report?.reason === 'disabled'
      ? t('The risk check is switched off on this site.')
      : t('GoPlus could not be reached.')

  return (
    <Wrap data-testid="token-risk" data-state={state}>
      <Row>
        <Source data-testid="token-risk-source" title={source}>
          {source}
        </Source>
        {state === 'unavailable' && (
          <RetryButton
            type="button"
            data-testid="token-risk-retry"
            aria-label={t('Check again')}
            title={t('Check again')}
            disabled={isValidating}
            onClick={() => mutate()}
          >
            <RefreshIcon color="primary" width="18px" height="18px" />
          </RetryButton>
        )}
        <Chip
          type="button"
          $tone={tone}
          data-testid="token-risk-chip"
          aria-expanded={open}
          aria-controls={panelId}
          title={DISCLAIMER}
          disabled={state === 'loading'}
          onClick={() => setOpen((o) => !o)}
        >
          <Dot $color={smallTax ? 'caution' : tone} $pulse={state === 'loading'} aria-hidden />
          <span>{label}</span>
          {state !== 'loading' && <ChevronDownIcon width="16px" height="16px" color="textSubtle" />}
        </Chip>
      </Row>
      {open && state !== 'loading' && (
        <Panel id={panelId} data-testid="token-risk-panel">
          {state === 'unavailable' && (
            <p style={{ margin: 0 }}>
              {reasonText} {t('There is no result, and that is not a pass. Try again in a minute.')}
            </p>
          )}
          {state === 'not_checked' && (
            <p style={{ margin: 0 }}>{t('GoPlus has no data for this token yet. That is not a pass.')}</p>
          )}
          {report?.status === 'ok' &&
            (report.flags.length ? (
              <FlagRows flags={report.flags} />
            ) : (
              <p style={{ margin: 0 }}>
                {t('GoPlus reported none of the flags it checks (honeypot, tax, owner powers, proxy, source code).')}
              </p>
            ))}
          {report?.facts && <FactsList facts={report.facts} />}
          <Disclaimer>{t(DISCLAIMER)}</Disclaimer>
          <Meta>
            {report?.checkedAt
              ? t('%source% · checked %time%', { source: 'GoPlus Security', time: formatTime(report.checkedAt, true) })
              : 'GoPlus Security'}
            {report?.stale && ` · ${t('GoPlus did not answer just now; this is its last result.')}`}
            {' · '}
            <Link
              external
              href={goPlusTokenPage(token.address)}
              style={{ display: 'inline-flex', fontSize: '12px', lineHeight: '16px' }}
            >
              {t('View on GoPlus')}
            </Link>
          </Meta>
        </Panel>
      )}
    </Wrap>
  )
}

const AccessRisk: React.FC<AccessRiskProps> = ({ token }) => {
  const [show] = useUserTokenRisk()
  if (!show || token?.chainId !== ChainId.BSC) return null
  return <GoPlusRiskCheck key={token.address} token={token} />
}

export default AccessRisk
